import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Users } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api'
import { ScoreBar } from '../components/ScoreBar'
import { TierBadge } from '../components/TierBadge'
import type { AgentListItem, Tier } from '../types'

type SortKey = 'tier_asc' | 'calls_desc' | 'avg_desc' | 'last_call_desc'

const SORT_LABELS: Record<SortKey, string> = {
  tier_asc: 'Tier (best first), then Avg',
  calls_desc: 'Calls (most first)',
  avg_desc: 'Avg overall (best first)',
  last_call_desc: 'Last call (most recent)',
}

const TIERS: Tier[] = [1, 2, 3, 4]

// Same palette as TierBadge / LeaderboardPage so the toggle pills stay consistent.
const TIER_PILL_ACTIVE: Record<Tier, string> = {
  1: 'bg-emerald-900/60 text-emerald-200 border-emerald-700',
  2: 'bg-sky-900/60 text-sky-200 border-sky-700',
  3: 'bg-amber-900/60 text-amber-200 border-amber-700',
  4: 'bg-red-900/60 text-red-200 border-red-700',
}

function sortAgents(rows: AgentListItem[], key: SortKey): AgentListItem[] {
  const copy = [...rows]
  copy.sort((a, b) => {
    switch (key) {
      case 'tier_asc': {
        const aTier = a.tier ?? Number.POSITIVE_INFINITY
        const bTier = b.tier ?? Number.POSITIVE_INFINITY
        if (aTier !== bTier) return aTier - bTier
        return (b.avg_overall ?? 0) - (a.avg_overall ?? 0)
      }
      case 'calls_desc':
        return b.calls - a.calls
      case 'avg_desc':
        return (b.avg_overall ?? 0) - (a.avg_overall ?? 0)
      case 'last_call_desc': {
        const aT = a.last_call_at ? Date.parse(a.last_call_at) : 0
        const bT = b.last_call_at ? Date.parse(b.last_call_at) : 0
        return bT - aT
      }
      default:
        return 0
    }
  })
  return copy
}

export function AgentsIndexPage() {
  const navigate = useNavigate()
  const { data, isLoading, error } = useQuery({
    queryKey: ['agents', 'list'],
    queryFn: () => api.agents.list(),
  })

  const [sortKey, setSortKey] = useState<SortKey>('tier_asc')
  const [tierFilter, setTierFilter] = useState<Set<Tier>>(new Set())
  const [campaignFilter, setCampaignFilter] = useState<string>('')
  const sortId = useId()
  const campaignId = useId()

  // Auto-populate campaign options from data.
  const campaigns = useMemo(() => {
    if (!data) return []
    const set = new Set<string>()
    for (const a of data) {
      if (a.campaign) set.add(a.campaign)
    }
    return Array.from(set).sort()
  }, [data])

  const filtered = useMemo(() => {
    if (!data) return []
    return data.filter((a) => {
      if (tierFilter.size > 0) {
        if (a.tier === null || a.tier === undefined) return false
        if (!tierFilter.has(a.tier)) return false
      }
      if (campaignFilter && a.campaign !== campaignFilter) return false
      return true
    })
  }, [data, tierFilter, campaignFilter])

  const sorted = useMemo(() => sortAgents(filtered, sortKey), [filtered, sortKey])

  const toggleTier = (t: Tier) => {
    setTierFilter((prev) => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  }

  const resetFilters = () => {
    setTierFilter(new Set())
    setCampaignFilter('')
  }

  const filtersActive = tierFilter.size > 0 || campaignFilter !== ''

  const goToAgent = (viciUser: string) => {
    navigate(`/agents/${encodeURIComponent(viciUser)}`)
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-2">
        <Users className="h-5 w-5 text-emerald-400" />
        <h1 className="text-xl font-semibold">Agents</h1>
      </header>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{(error as Error).message}</span>
        </div>
      )}

      {!isLoading && data && data.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
          <div className="flex items-center gap-2">
            <label
              htmlFor={sortId}
              className="text-xs font-medium uppercase tracking-wide text-slate-400"
            >
              Sort
            </label>
            <select
              id={sortId}
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200 focus:border-indigo-500 focus:outline-none"
            >
              {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABELS[k]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Tier
            </span>
            <div className="flex gap-1">
              {TIERS.map((t) => {
                const active = tierFilter.has(t)
                const cls = active
                  ? TIER_PILL_ACTIVE[t]
                  : 'bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800'
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTier(t)}
                    aria-pressed={active}
                    className={`rounded border px-2 py-0.5 font-mono text-xs transition-colors ${cls}`}
                  >
                    T{t}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label
              htmlFor={campaignId}
              className="text-xs font-medium uppercase tracking-wide text-slate-400"
            >
              Campaign
            </label>
            <select
              id={campaignId}
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200 focus:border-indigo-500 focus:outline-none"
            >
              <option value="">All</option>
              {campaigns.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {filtersActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="ml-auto rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800"
            >
              Reset
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="h-40 animate-pulse rounded bg-slate-800/60" />
      ) : !data || data.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-8 text-center">
          <p className="text-sm text-slate-400">
            No calls yet. Run{' '}
            <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-xs text-slate-200">
              python -m killacoach.runner.cli --agent 1201 --limit 50
            </code>
            .
          </p>
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-8 text-center">
          <p className="text-sm text-slate-400">
            No agents match your filters.{' '}
            <button
              type="button"
              onClick={resetFilters}
              className="font-medium text-indigo-300 underline-offset-2 hover:underline"
            >
              Reset
            </button>
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900/40">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-xs text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">User</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Campaign</th>
                <th className="px-3 py-2 text-left">Tier</th>
                <th className="px-3 py-2 text-right">Calls</th>
                <th className="px-3 py-2 text-left">Avg overall</th>
                <th className="px-3 py-2 text-left">Last call</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => (
                <tr
                  key={a.vici_user}
                  tabIndex={0}
                  aria-label={`View ${a.vici_user} agent detail`}
                  onClick={() => goToAgent(a.vici_user)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      goToAgent(a.vici_user)
                    }
                  }}
                  className="cursor-pointer border-t border-slate-800 transition-colors hover:bg-slate-800/30 focus:bg-slate-800/40 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <td className="px-3 py-2">
                    <span className="font-medium text-slate-100">{a.vici_user}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-300">{a.full_name ?? '--'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">
                    {a.campaign ?? '--'}
                  </td>
                  <td className="px-3 py-2">
                    <TierBadge
                      tier={a.tier ?? null}
                      tierName={a.tier_name}
                      variant="compact"
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-300">{a.calls}</td>
                  <td className="px-3 py-2">
                    <ScoreBar value={a.avg_overall} />
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {a.last_call_at ? new Date(a.last_call_at).toLocaleString() : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
